import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  Clock,
  Inbox,
  ArrowRight,
  List,
  MessageSquare,
  Plus,
  Users,
} from 'lucide-react';
import { PageHeader, EmptyState, ErrorState, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { HoverCard } from '@/components/ui/hover-card';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { formatDate, formatDateTime, timeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useAddTaskRemark, useMyDay, useTaskCalendar } from './api';
import {
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_VARIANT,
  TASK_STATUS_LABELS,
  TASK_TYPE_LABELS,
} from './labels';
import { TaskDialog } from './task-dialog';
import { ConfirmDoneDialog, TaskDetailsDialog } from './task-details-dialog';
import { WorkState } from './task-work-state';
import type { Task } from './types';

/** "2026-09-12" for a date, in the reader's own zone rather than UTC. */
function dayKey(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Just the clock part, for a calendar chip that has no room for a date. */
const formatTime = (iso: string) => formatDate(iso, 'HH:mm');

/**
 * The same details as the hover card, as plain text — the browser's own
 * tooltip, so a chip still explains itself if anything about ours goes wrong.
 */
function summarise(task: Task, nameOf: (id: string) => string): string {
  const lines = [task.title];
  if (task.dueAt) lines.push(`Due: ${formatDateTime(task.dueAt)}`);
  lines.push(`${TASK_TYPE_LABELS[task.type]} · ${TASK_PRIORITY_LABELS[task.priority]} · ${TASK_STATUS_LABELS[task.status]}`);
  lines.push(`Responsible: ${task.assignee?.name ?? 'Unassigned'}`);
  if (task.participantIds.length > 0) {
    lines.push(`Also told: ${task.participantIds.map(nameOf).join(', ')}`);
  }
  if (task.description) lines.push('', task.description);
  return lines.join('\n');
}

/** A stripe of colour per priority, so a month reads at a glance. */
const PRIORITY_STRIPE: Record<Task['priority'], string> = {
  LOW: 'border-l-slate-300',
  MEDIUM: 'border-l-sky-400',
  HIGH: 'border-l-amber-500',
  URGENT: 'border-l-destructive',
};

/**
 * Everything a task is, for the card that opens when you point at one on the
 * calendar — the whole point being that you should not have to click through
 * to find out what "Demo call" at 14:00 actually involves.
 */
function TaskCard({ task, nameOf }: { task: Task; nameOf: (id: string) => string }) {
  const done = task.status === 'DONE' || task.status === 'CANCELLED';

  return (
    <div className="space-y-2 text-left">
      <div className="flex items-start gap-2">
        <span className={cn('text-sm font-semibold leading-snug', done && 'line-through')}>
          {task.title}
        </span>
        {done && (
          <Badge variant="secondary" className="ml-auto shrink-0 text-[10px]">
            {TASK_STATUS_LABELS[task.status]}
          </Badge>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant={TASK_PRIORITY_VARIANT[task.priority]} className="text-[10px]">
          {TASK_TYPE_LABELS[task.type]}
        </Badge>
        <Badge variant="secondary" className="text-[10px]">
          {TASK_PRIORITY_LABELS[task.priority]}
        </Badge>
        {!done && (
          <Badge variant="secondary" className="text-[10px]">
            {TASK_STATUS_LABELS[task.status]}
          </Badge>
        )}
      </div>

      {task.description && (
        <p className="whitespace-pre-wrap text-xs text-muted-foreground">{task.description}</p>
      )}

      <dl className="space-y-1 text-xs">
        {task.dueAt && (
          <Row label="Due">
            {formatDateTime(task.dueAt)}
            {!done && new Date(task.dueAt) < new Date() && (
              <span className="ml-1 text-destructive">· {timeAgo(task.dueAt)} overdue</span>
            )}
          </Row>
        )}
        <Row label="Assigned to">
          {task.assignee?.name ?? 'Unassigned'}
          {task.assignedAt && (
            <span className="text-muted-foreground"> · {formatDate(task.assignedAt, 'd MMM, HH:mm')}</span>
          )}
        </Row>
        {task.participantIds.length > 0 && (
          <Row label="Also told">{task.participantIds.map(nameOf).join(', ')}</Row>
        )}
        <Row label="Created">
          {formatDate(task.createdAt, 'd MMM, HH:mm')}
          {task.createdBy && <span className="text-muted-foreground"> by {task.createdBy.name}</span>}
        </Row>
        {!done && (
          <Row label="Progress">
            <WorkState task={task} />
          </Row>
        )}
        {task.entityType && task.entityId && (
          <Row label="Linked to">{task.entityType}</Row>
        )}
      </dl>
    </div>
  );
}

/** Everything on one day, for the card that opens over its number. */
function DayCard({
  dayKey: key,
  tasks,
  nameOf,
}: {
  dayKey: string;
  tasks: Task[];
  nameOf: (id: string) => string;
}) {
  return (
    <div className="max-h-80 space-y-3 overflow-y-auto">
      <p className="text-xs font-semibold">
        {formatDate(`${key}T00:00:00`)} · {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
      </p>
      {tasks.map((task) => (
        <div key={task.id} className="border-t pt-2 first:border-t-0 first:pt-0">
          <TaskCard task={task} nameOf={nameOf} />
        </div>
      ))}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-20 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 flex-1">{children}</dd>
    </div>
  );
}

/** The 42 days a month grid shows, starting on the Monday before the 1st. */
function monthGrid(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  // getDay() is Sunday-first; the grid is Monday-first.
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - offset);
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

/**
 * "My day" (spec ACT-02).
 *
 * Two ways to read the same work: a list in the order you would work it —
 * overdue, today, the week, and what got finished — and a month you can click
 * into. A task belongs to whoever is responsible for it, to everybody brought
 * along with it, and to whoever handed it out, so what somebody adds for a
 * colleague turns up on both their days.
 */
export function MyDayPage() {
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [month, setMonth] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(() => dayKey(new Date()));
  const [creatingOn, setCreatingOn] = useState<string | null>(null);
  const [editing, setEditing] = useState<Task | null>(null);
  /** The task whose details are open. Clicking a task reads it; editing is a step further. */
  const [viewing, setViewing] = useState<Task | null>(null);
  /** The task waiting on "are you sure" before it is marked done. */
  const [confirming, setConfirming] = useState<Task | null>(null);

  const { data, isLoading } = useMyDay();
  const finish = useAddTaskRemark();

  // The month on screen, both ends inclusive, in one request. The grid starts
  // on the Monday before the 1st and runs past the last, so the window is the
  // whole grid — otherwise the days either side would look empty.
  const grid = useMemo(() => monthGrid(month), [month]);
  const from = grid[0];
  const to = new Date(grid[grid.length - 1]);
  to.setHours(23, 59, 59, 999);

  const {
    data: monthTasks,
    error: calendarError,
    refetch: refetchCalendar,
  } = useTaskCalendar(from.toISOString(), to.toISOString());

  // Participants are stored as ids; the card shows names.
  const { data: users = [] } = useUserOptions();
  const nameOf = useMemo(() => {
    const names = new Map(users.map((user) => [user.id, user.name]));
    return (id: string) => names.get(id) ?? 'Someone';
  }, [users]);

  const byDay = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const task of monthTasks?.tasks ?? []) {
      if (!task.dueAt) continue;
      const key = dayKey(new Date(task.dueAt));
      map.set(key, [...(map.get(key) ?? []), task]);
    }
    return map;
  }, [monthTasks]);

  const total =
    (data?.overdue.length ?? 0) + (data?.today.length ?? 0) + (data?.upcoming.length ?? 0);
  const today = dayKey(new Date());
  const dayTasks = byDay.get(selectedDay) ?? [];

  return (
    <div>
      <PageHeader
        title="My day"
        description={
          total === 0
            ? 'Nothing needs you right now.'
            : `${total} ${total === 1 ? 'thing' : 'things'} on your plate.`
        }
        actions={
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border p-0.5">
              <ViewTab active={view === 'list'} onClick={() => setView('list')} icon={<List className="h-4 w-4" />}>
                List
              </ViewTab>
              <ViewTab
                active={view === 'calendar'}
                onClick={() => setView('calendar')}
                icon={<CalendarDays className="h-4 w-4" />}
              >
                Calendar
              </ViewTab>
            </div>
            <Can permission="crm.task.create">
              <Button onClick={() => setCreatingOn(view === 'calendar' ? selectedDay : today)}>
                <Plus className="h-4 w-4" />
                New task
              </Button>
            </Can>
          </div>
        }
      />

      {isLoading ? (
        <TableSkeleton />
      ) : view === 'calendar' ? (
        <div className="grid gap-5 lg:grid-cols-[1fr_20rem]">
          <Card className="p-3">
            <div className="mb-3 flex items-center gap-2 px-1">
              <h2 className="text-sm font-semibold">
                {month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
              </h2>
              <div className="ml-auto flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  aria-label="Previous month"
                  onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setMonth(new Date())}>
                  Today
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  aria-label="Next month"
                  onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* A calendar that quietly shows nothing is worse than one that
                says it could not load. */}
            {calendarError && (
              <div className="mb-2">
                <ErrorState
                  message="Could not load this month's tasks."
                  onRetry={() => void refetchCalendar()}
                />
              </div>
            )}

            <div className="grid grid-cols-7 gap-1 px-1 pb-1 text-center text-[11px] font-medium text-muted-foreground">
              {WEEKDAYS.map((day) => (
                <div key={day}>{day}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {grid.map((day) => {
                const key = dayKey(day);
                const tasks = byDay.get(key) ?? [];
                const outside = day.getMonth() !== month.getMonth();
                const open = tasks.filter((task) => task.status !== 'DONE' && task.status !== 'CANCELLED');

                return (
                  // A div, not a button: the chips inside are buttons of their
                  // own, and a button inside a button is not a thing.
                  <div
                    key={key}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedDay(key)}
                    onDoubleClick={() => setCreatingOn(key)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedDay(key);
                      }
                    }}
                    className={cn(
                      'flex h-20 cursor-pointer flex-col rounded-md border p-1 text-left transition-colors hover:border-primary/50',
                      outside && 'opacity-40',
                      key === selectedDay ? 'border-primary bg-primary/5' : 'bg-background',
                    )}
                  >
                    {tasks.length > 0 ? (
                      <HoverCard content={<DayCard dayKey={key} tasks={tasks} nameOf={nameOf} />}>
                        <span
                          className={cn(
                            'w-fit text-xs',
                            key === today
                              ? 'flex h-5 w-5 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground'
                              : 'font-medium underline decoration-dotted underline-offset-2',
                          )}
                        >
                          {day.getDate()}
                        </span>
                      </HoverCard>
                    ) : (
                      <span
                        className={cn(
                          'text-xs',
                          key === today
                            ? 'flex h-5 w-5 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground'
                            : 'font-medium',
                        )}
                      >
                        {day.getDate()}
                      </span>
                    )}
                    <div className="mt-0.5 min-h-0 flex-1 space-y-0.5 overflow-hidden">
                      {tasks.slice(0, 2).map((task) => (
                        <HoverCard
                          key={task.id}
                          content={<TaskCard task={task} nameOf={nameOf} />}
                        >
                          <button
                            type="button"
                            title={summarise(task, nameOf)}
                            onClick={(event) => {
                              // Opening the task, not the day underneath it.
                              event.stopPropagation();
                              setViewing(task);
                            }}
                            className={cn(
                              'block w-full truncate rounded border-l-2 bg-muted px-1 text-left text-[10px] leading-4 hover:bg-accent',
                              PRIORITY_STRIPE[task.priority],
                              (task.status === 'DONE' || task.status === 'CANCELLED') &&
                                'text-muted-foreground line-through',
                            )}
                          >
                            {task.dueAt ? `${formatTime(task.dueAt)} ` : ''}
                            {task.title}
                          </button>
                        </HoverCard>
                      ))}
                      {tasks.length > 2 && (
                        <HoverCard content={<DayCard dayKey={key} tasks={tasks} nameOf={nameOf} />}>
                          <span className="block px-1 text-[10px] text-muted-foreground underline decoration-dotted">
                            +{tasks.length - 2} more
                          </span>
                        </HoverCard>
                      )}
                    </div>
                    {open.length > 0 && (
                      <span className="text-[10px] text-muted-foreground">
                        {open.length} open
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <CalendarDays className="h-4 w-4 text-primary" />
                {formatDate(`${selectedDay}T00:00:00`)}
                <span className="ml-auto text-sm font-normal text-muted-foreground">
                  {dayTasks.length}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Can permission="crm.task.create">
                <Button variant="outline" className="w-full" onClick={() => setCreatingOn(selectedDay)}>
                  <Plus className="h-4 w-4" />
                  Add task on this date
                </Button>
              </Can>

              {dayTasks.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  Nothing on this date.
                </p>
              ) : (
                dayTasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    nameOf={nameOf}
                    onDone={() => setConfirming(task)}
                    onOpen={() => setViewing(task)}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </div>
      ) : total === 0 && (data?.completed.length ?? 0) === 0 ? (
        <EmptyState
          icon={<CheckCircle2 className="h-8 w-8 text-emerald-600" />}
          title="All clear"
          description="No overdue work and nothing due this week. Add a task, or open the calendar to plan one for a day ahead."
          action={
            <Can permission="crm.task.create">
              <Button onClick={() => setCreatingOn(today)}>
                <Plus className="h-4 w-4" />
                New task
              </Button>
            </Can>
          }
        />
      ) : (
        <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Column
            title="Overdue"
            icon={<Clock className="h-4 w-4 text-destructive" />}
            tasks={data?.overdue ?? []}
            emptyText="Nothing overdue."
            nameOf={nameOf}
            onDone={setConfirming}
            onOpen={setViewing}
            tone="destructive"
          />
          <Column
            title="Today"
            icon={<CalendarClock className="h-4 w-4 text-primary" />}
            tasks={data?.today ?? []}
            emptyText="Nothing due today."
            nameOf={nameOf}
            onDone={setConfirming}
            onOpen={setViewing}
          />
          <Column
            title="Next 7 days"
            icon={<Inbox className="h-4 w-4 text-muted-foreground" />}
            tasks={data?.upcoming ?? []}
            emptyText="Nothing scheduled."
            nameOf={nameOf}
            onDone={setConfirming}
            onOpen={setViewing}
          />
          {/* On the right, what got finished — the column that makes the other
              three feel like they are going somewhere. */}
          <Column
            title="Completed"
            icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
            tasks={data?.completed ?? []}
            emptyText="Nothing finished yet."
            nameOf={nameOf}
            onDone={setConfirming}
            onOpen={setViewing}
            tone="done"
          />
        </div>
      )}

      {creatingOn && <TaskDialog defaultDate={creatingOn} onClose={() => setCreatingOn(null)} />}
      {editing && <TaskDialog task={editing} onClose={() => setEditing(null)} />}

      {viewing && (
        <TaskDetailsDialog
          task={viewing}
          nameOf={nameOf}
          onClose={() => setViewing(null)}
          onEdit={() => {
            setEditing(viewing);
            setViewing(null);
          }}
        />
      )}

      {confirming && (
        <ConfirmDoneDialog
          task={confirming}
          pending={finish.isPending}
          error={finish.error instanceof ApiRequestError ? finish.error.message : null}
          onCancel={() => {
            finish.reset();
            setConfirming(null);
          }}
          // Through the update route, so whoever was given the task can finish
          // it even when their role cannot edit tasks in general.
          onConfirm={() =>
            finish.mutate(
              { taskId: confirming.id, status: 'DONE' },
              { onSuccess: () => setConfirming(null) },
            )
          }
        />
      )}
    </div>
  );
}

function ViewTab({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded px-2.5 py-1 text-sm transition-colors',
        active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function Column({
  title,
  icon,
  tasks,
  emptyText,
  nameOf,
  onDone,
  onOpen,
  tone,
}: {
  title: string;
  icon: React.ReactNode;
  tasks: Task[];
  emptyText: string;
  nameOf: (id: string) => string;
  onDone: (task: Task) => void;
  onOpen: (task: Task) => void;
  tone?: 'destructive' | 'done';
}) {
  return (
    <Card>
      <CardHeader className="px-3 pb-2 pt-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          {icon}
          {title}
          <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
            {tasks.length}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5 px-3 pb-3">
        {tasks.length === 0 ? (
          <p className="py-3 text-center text-xs text-muted-foreground">{emptyText}</p>
        ) : (
          tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              tone={tone}
              nameOf={nameOf}
              onDone={() => onDone(task)}
              onOpen={() => onOpen(task)}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

function TaskRow({
  task,
  tone,
  nameOf,
  onDone,
  onOpen,
}: {
  task: Task;
  tone?: 'destructive' | 'done';
  /** Resolves participant ids to names. */
  nameOf?: (id: string) => string;
  onDone: () => void;
  onOpen: () => void;
}) {
  const done = task.status === 'DONE' || task.status === 'CANCELLED';
  // A description that only repeats the title is noise on a card this small.
  const description =
    task.description && task.description.trim() !== task.title.trim() ? task.description : null;
  const handedOn = Boolean(task.createdBy && task.assignee && task.createdBy.id !== task.assignee.id);

  return (
    <div
      className={cn(
        'rounded-md border px-2.5 py-2 transition-colors hover:bg-muted/40',
        tone === 'destructive' && 'border-destructive/30 bg-destructive/5',
        done && 'opacity-70',
      )}
    >
      <div className="flex items-start gap-2">
        <button
          onClick={onDone}
          disabled={done}
          title={done ? 'Done' : 'Mark done'}
          className="mt-0.5 shrink-0 text-muted-foreground transition-colors hover:text-emerald-600 disabled:hover:text-muted-foreground"
        >
          {done ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          ) : (
            <Circle className="h-4 w-4" />
          )}
        </button>

        <button onClick={onOpen} className="min-w-0 flex-1 space-y-1 text-left">
          <div className={cn('text-sm font-medium leading-snug', done && 'line-through')}>
            {task.title}
          </div>
          {description && (
            <p className="line-clamp-1 text-xs text-muted-foreground">{description}</p>
          )}

          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
            <Badge variant={TASK_PRIORITY_VARIANT[task.priority]} className="px-1.5 py-0 text-[10px]">
              {TASK_TYPE_LABELS[task.type]}
            </Badge>
            {task.dueAt && !done && (
              <span className={cn(tone === 'destructive' && 'font-medium text-destructive')}>
                {tone === 'destructive'
                  ? `${timeAgo(task.dueAt)} overdue`
                  : `Due ${formatDate(task.dueAt, 'd MMM, HH:mm')}`}
              </span>
            )}
          </div>

          {/* Who gave it to whom — the line that tells a manager whose court
              the ball is in. */}
          <div className="flex flex-wrap items-center gap-x-1 text-[11px] text-muted-foreground">
            {handedOn ? (
              <>
                <span>{task.createdBy!.name}</span>
                <ArrowRight className="h-3 w-3" />
                <span className="font-medium text-foreground">{task.assignee!.name}</span>
              </>
            ) : (
              <span className="font-medium text-foreground">{task.assignee?.name ?? 'Unassigned'}</span>
            )}
            {task.participantIds.length > 0 && (
              <span
                className="ml-1 flex items-center gap-0.5"
                title={
                  nameOf
                    ? `Also told: ${task.participantIds.map(nameOf).join(', ')}`
                    : `${task.participantIds.length} colleagues told`
                }
              >
                <Users className="h-3 w-3" />
                {nameOf ? task.participantIds.map(nameOf).join(', ') : task.participantIds.length}
              </span>
            )}
          </div>

          {/* Two lines, not one that wraps: in a narrow column a joined line
              breaks at the separator and leaves it hanging. */}
          <div className="space-y-0.5 text-[11px] text-muted-foreground">
            <div className="flex items-center gap-2" title={`Created ${formatDateTime(task.createdAt)}`}>
              <span>Created {formatDate(task.createdAt, 'd MMM, HH:mm')}</span>
              {(task._count?.remarks ?? 0) > 0 && (
                <span className="flex items-center gap-0.5" title="Updates on this task">
                  <MessageSquare className="h-3 w-3" />
                  {task._count!.remarks}
                </span>
              )}
            </div>
            <WorkState task={task} />
          </div>
        </button>

        {task.entityType === 'deal' && task.entityId && (
          <Link
            to={`/crm/deals/${task.entityId}`}
            className="shrink-0 text-[11px] text-primary hover:underline"
          >
            Deal
          </Link>
        )}
      </div>
    </div>
  );
}

